import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);
export const useNotifications = () => useContext(NotificationContext);

const API_URL = process.env.REACT_APP_BACKEND_URL;
const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

function isNotificationSupported() {
  return 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
}

function arrayBufferToBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
  // Use URL-safe base64 encoding (required by Web Push spec)
  return window.btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

// Send subscription to backend
async function syncSubscription(sub, token) {
  if (!sub || !token) return;
  try {
    await fetch(`${API_URL}/api/push/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({
        endpoint: sub.endpoint,
        keys: {
          p256dh: arrayBufferToBase64(sub.getKey('p256dh')),
          auth: arrayBufferToBase64(sub.getKey('auth'))
        }
      })
    });
  } catch (e) {
    console.error('Failed to sync push subscription:', e);
  }
}

// Subscribe or re-subscribe to push using a registration directly
async function ensurePushSubscription(reg, token) {
  if (!reg || !token || !VAPID_PUBLIC_KEY) return null;
  try {
    let sub = await reg.pushManager.getSubscription();
    // Always re-sync to ensure backend has the latest valid subscription
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
    }
    await syncSubscription(sub, token);
    return sub;
  } catch (e) {
    // If existing subscription is invalid, unsubscribe and retry
    try {
      const oldSub = await reg.pushManager.getSubscription();
      if (oldSub) await oldSub.unsubscribe();
      const newSub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });
      await syncSubscription(newSub, token);
      return newSub;
    } catch (retryErr) {
      console.error('Push subscription retry failed:', retryErr);
      return null;
    }
  }
}

export const NotificationProvider = ({ children }) => {
  const { user, token, isSubscribed } = useAuth();
  const [counts, setCounts] = useState({ total: 0 });
  const [permissionStatus, setPermissionStatus] = useState('default');
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const prevCounts = useRef({});
  const onCountsChangeRef = useRef(null);
  const swRegRef = useRef(null);

  // Helper: update app badge via both navigator API and SW fallback
  // Only sets badge when app is NOT in foreground (badge = "come back to the app")
  const updateBadge = useCallback((count) => {
    // Only set badge when app is in background or hidden
    // When visible, badge should stay cleared (user is looking at the app)
    if (document.visibilityState === 'visible' && document.hasFocus()) return;
    
    if ('setAppBadge' in navigator) {
      if (count > 0) {
        navigator.setAppBadge(count).catch(() => {});
      } else {
        navigator.clearAppBadge().catch(() => {});
      }
    }
    // Also tell the SW to set badge (works as fallback for background)
    if (swRegRef.current && swRegRef.current.active) {
      try {
        swRegRef.current.active.postMessage({ type: 'SET_BADGE', count });
      } catch (e) {
        // postMessage can fail if SW is in a transitional state
        console.warn('Badge postMessage failed:', e.message);
      }
    }
  }, []);

  // Clear badge when app gains focus (user opened the PWA)
  useEffect(() => {
    const clearBadgeOnFocus = () => {
      if ('clearAppBadge' in navigator) {
        navigator.clearAppBadge().catch(() => {});
      }
    };
    const reSyncPushOnFocus = async () => {
      if (!document.hidden && swRegRef.current && token && Notification.permission === 'granted') {
        const sub = await ensurePushSubscription(swRegRef.current, token);
        if (sub) setPushSubscribed(true);
      }
      clearBadgeOnFocus();
    };
    window.addEventListener('focus', reSyncPushOnFocus);
    document.addEventListener('visibilitychange', reSyncPushOnFocus);
    clearBadgeOnFocus();
    return () => {
      window.removeEventListener('focus', reSyncPushOnFocus);
      document.removeEventListener('visibilitychange', reSyncPushOnFocus);
    };
  }, [token]);

  // Register service worker + auto-subscribe if permission already granted
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker.register('/sw.js').then(async (reg) => {
      swRegRef.current = reg;

      // Listen for SW messages (e.g. NAVIGATE from notification click)
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'NAVIGATE' && event.data.url) {
          window.location.href = event.data.url;
        }
      });

      // Check for SW updates
      reg.update().catch(() => {});

      if (!isNotificationSupported()) {
        setPermissionStatus('unsupported');
        return;
      }

      setPermissionStatus(Notification.permission);

      // Auto-subscribe if permission already granted (critical for PWA reopen)
      if (Notification.permission === 'granted' && token) {
        const sub = await ensurePushSubscription(reg, token);
        if (sub) setPushSubscribed(true);
      }
    }).catch((err) => console.error('SW registration failed:', err));
  }, [token]);

  // Show permission prompt for new users
  useEffect(() => {
    if (user && isSubscribed && isNotificationSupported()) {
      if (Notification.permission === 'default') {
        const dismissed = sessionStorage.getItem('vf_notif_dismissed');
        if (dismissed !== 'true') {
          const timer = setTimeout(() => setShowPermissionPrompt(true), 3000);
          return () => clearTimeout(timer);
        }
      }
    }
  }, [user, isSubscribed]);

  // Request permission and subscribe
  const requestPermission = useCallback(async () => {
    if (!isNotificationSupported()) return;
    try {
      const result = await Notification.requestPermission();
      setPermissionStatus(result);
      setShowPermissionPrompt(false);
      if (result === 'granted') {
        const reg = swRegRef.current;
        if (reg && token) {
          const sub = await ensurePushSubscription(reg, token);
          if (sub) setPushSubscribed(true);
        }
      }
    } catch (err) {
      console.error('Permission request failed:', err);
    }
  }, [token]);

  const dismissPrompt = useCallback(() => {
    setShowPermissionPrompt(false);
    sessionStorage.setItem('vf_notif_dismissed', 'true');
  }, []);

  // Fetch notification counts
  const fetchCounts = useCallback(async () => {
    if (!token || !user) return;
    try {
      const res = await fetch(`${API_URL}/api/notifications/count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) return;
      const newCounts = await res.json();

      // Send push-style notifications via SW for new items (works in PWA)
      if (Notification.permission === 'granted' && swRegRef.current) {
        const prev = prevCounts.current;
        if (prev.total !== undefined) {
          if ((newCounts.unread_chats || 0) > (prev.unread_chats || 0)) {
            swRegRef.current.showNotification('New Chat Message', {
              body: 'You have a new message in your collaboration',
              icon: '/icons/icon-192x192.png',
              tag: 'vf-chat',
              renotify: true,
              data: { url: '/dashboard' }
            }).catch(() => {});
          }
          if ((newCounts.pending_collab_invites || 0) > (prev.pending_collab_invites || 0)) {
            swRegRef.current.showNotification('New Collaboration Request', {
              body: 'Someone wants to collaborate with you!',
              icon: '/icons/icon-192x192.png',
              tag: 'vf-collab',
              renotify: true,
              data: { url: '/dashboard' }
            }).catch(() => {});
          }
          if ((newCounts.pending_applications || 0) > (prev.pending_applications || 0)) {
            swRegRef.current.showNotification('New Campaign Application', {
              body: 'An influencer applied to your campaign!',
              icon: '/icons/icon-192x192.png',
              tag: 'vf-campaign-app',
              renotify: true,
              data: { url: '/dashboard' }
            }).catch(() => {});
          }
        }
      }

      // Update app badge (PWA icon badge on desktop/mobile)
      const badgeCount = newCounts.total || 0;
      updateBadge(badgeCount);

      // Detect changes and trigger refresh callbacks
      const prevState = prevCounts.current;
      const changed = prevState.total !== undefined && (
        prevState.pending_applications !== newCounts.pending_applications ||
        prevState.pending_content_review !== newCounts.pending_content_review ||
        prevState.pending_collab_invites !== newCounts.pending_collab_invites ||
        prevState.accepted_applications !== newCounts.accepted_applications ||
        prevState.unread_chats !== newCounts.unread_chats ||
        prevState.content_approved !== newCounts.content_approved ||
        prevState.content_rejected !== newCounts.content_rejected
      );
      prevCounts.current = newCounts;
      setCounts(newCounts);
      if (changed && onCountsChangeRef.current) onCountsChangeRef.current(newCounts);
    } catch (err) {
      console.error('Failed to fetch notification counts:', err);
    }
  }, [token, user, updateBadge]);

  const markViewed = useCallback(async (type) => {
    if (!token) return;
    try {
      await fetch(`${API_URL}/api/notifications/mark-viewed`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ type })
      });
      fetchCounts();
    } catch (err) {
      console.error('Failed to mark viewed:', err);
    }
  }, [token, fetchCounts]);

  // Polling with visibility + focus + pageshow handling for PWA
  useEffect(() => {
    if (!token || !user) return;
    fetchCounts();

    let interval;
    const start = () => { if (!interval) interval = setInterval(fetchCounts, 5000); };
    const stop = () => { clearInterval(interval); interval = null; };
    const onVisibility = () => { document.hidden ? stop() : (fetchCounts(), start()); };
    const onFocus = () => { fetchCounts(); if (!interval) start(); };
    // pageshow fires when PWA is restored from bfcache (critical for iOS PWA)
    const onPageShow = (e) => {
      if (e.persisted) { fetchCounts(); if (!interval) start(); }
    };

    start();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('focus', onFocus);
    window.addEventListener('pageshow', onPageShow);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [token, user, fetchCounts]);

  const testPush = useCallback(async () => {
    if (!token) return { success: false, error: 'Not logged in' };
    try {
      const res = await fetch(`${API_URL}/api/push/test`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      return await res.json();
    } catch (e) {
      return { success: false, error: e.message };
    }
  }, [token]);

  const setOnCountsChange = useCallback((cb) => {
    onCountsChangeRef.current = cb;
  }, []);

  return (
    <NotificationContext.Provider value={{
      counts,
      permissionStatus,
      showPermissionPrompt,
      pushSubscribed,
      requestPermission,
      dismissPrompt,
      markViewed,
      fetchCounts,
      setOnCountsChange,
      testPush
    }}>
      {children}
    </NotificationContext.Provider>
  );
};
